import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Menu, MessageCircle, Phone, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { BrandLogo } from '@/components/generals/brand-mark'
import { buttonClasses } from '@/components/generals/button-link'
import { contactInfo, mainNav } from '@/constants/site'

const isActive = (pathname: string, href: string): boolean => {
    if (href === '/') return pathname === '/'

    return pathname === href || pathname.startsWith(`${href}/`)
}

export const Header: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        document.addEventListener('keydown', onKeyDown)
        document.body.style.overflow = 'hidden'

        return () => {
            document.removeEventListener('keydown', onKeyDown)
            document.body.style.overflow = ''
        }
    }, [open])

    const primaryPhone = contactInfo.phones[0]

    return (
        <header className='sticky top-0 z-50 border-b border-gold/15 bg-forest-950/95 text-cream backdrop-blur-md'>
            <div className='mx-auto flex h-20 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8'>
                <BrandLogo onNavigate={() => setOpen(false)} />

                <nav aria-label='منوی اصلی' className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        {mainNav.map(item => {
                            const active = isActive(router.pathname, item.href)

                            return (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        aria-current={active ? 'page' : undefined}
                                        className={cn(
                                            'relative block rounded-full px-3.5 py-2 text-[0.95rem] font-medium transition-colors xl:px-4',
                                            'after:absolute after:bottom-0 after:left-1/2 after:size-1 after:-translate-x-1/2 after:rotate-45 after:bg-gold after:transition-opacity',
                                            active ? 'text-gold after:opacity-100' : 'text-cream/80 after:opacity-0 hover:text-cream'
                                        )}
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <div className='flex items-center gap-2'>
                    <Link href='/contact' className={buttonClasses('gold', 'sm', 'hidden sm:inline-flex')}>
                        استعلام قیمت
                    </Link>

                    <button
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? 'بستن منو' : 'باز کردن منو'}
                        className='inline-flex size-11 items-center justify-center rounded-full border border-cream/15 text-cream transition-colors hover:border-gold hover:text-gold lg:hidden'
                    >
                        {open ? <X className='size-5' /> : <Menu className='size-5' />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    id='mobile-menu'
                    className='absolute inset-x-0 top-full h-[calc(100dvh-5rem)] overflow-y-auto border-t border-gold/15 bg-forest-950 animate-in fade-in slide-in-from-top-2 duration-200 lg:hidden'
                >
                    <nav aria-label='منوی موبایل' className='mx-auto max-w-7xl px-5 py-6 sm:px-8'>
                        <ul className='divide-y divide-cream/10'>
                            {mainNav.map(item => {
                                const active = isActive(router.pathname, item.href)

                                return (
                                    <li key={item.href}>
                                        <Link
                                            href={item.href}
                                            onClick={() => setOpen(false)}
                                            aria-current={active ? 'page' : undefined}
                                            className={cn(
                                                'flex items-center justify-between py-4 font-heading text-2xl font-bold',
                                                active ? 'text-gold' : 'text-cream'
                                            )}
                                        >
                                            {item.label}
                                            <span aria-hidden='true' className='size-1.5 rotate-45 bg-gold/70' />
                                        </Link>
                                    </li>
                                )
                            })}
                        </ul>

                        <div className='mt-8 grid gap-3 sm:grid-cols-2'>
                            <a href={`tel:${primaryPhone.tel}`} className={buttonClasses('outline-light', 'md')}>
                                <Phone aria-hidden='true' />
                                <span dir='ltr'>{primaryPhone.display}</span>
                            </a>

                            <a
                                href={contactInfo.whatsapp.url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className={buttonClasses('gold', 'md')}
                            >
                                <MessageCircle aria-hidden='true' />
                                گفتگو در واتس‌اپ
                            </a>
                        </div>
                    </nav>
                </div>
            )}
        </header>
    )
}
